import { useMemo, type ReactNode } from 'react';
import { LogtoProvider, type LogtoConfig, UserScope, ReservedScope, useLogto } from '@logto/react';
import LogtoClient, { type SignInOptions } from '@logto/browser';
import { LogtoAdapterContext, type LogtoAdapterValue } from './types';
import { logger } from '@utils/logger';

// ---------------------------------------------------------------------------
// Logto config -- moved from RootLayout.tsx (zero behavior change)
// ---------------------------------------------------------------------------

const logtoResources: string = import.meta.env.VITE_LOGTO_RESOURCE || '';
if (!logtoResources) {
  logger.error(
    '[WebLogtoAdapterProvider] VITE_LOGTO_RESOURCE is not set. Token requests will fail.',
  );
}

const webAppId = import.meta.env.VITE_LOGTO_APP_ID || '';
if (!webAppId) {
  logger.error('[WebLogtoAdapterProvider] VITE_LOGTO_APP_ID is not set. Web auth will fail.');
}

const logtoConfig: LogtoConfig = {
  endpoint: import.meta.env.VITE_LOGTO_ENDPOINT || 'https://login.meetball.fun',
  appId: webAppId,
  scopes: [
    UserScope.Email,
    UserScope.Phone,
    UserScope.CustomData,
    UserScope.Identities,
    UserScope.Profile,
    ReservedScope.OfflineAccess,
  ],
  resources: [logtoResources],
};

// ---------------------------------------------------------------------------
// signIn failures -- @logto/react swallows them, this puts them back
// ---------------------------------------------------------------------------

/**
 * The promise of the most recent `LogtoClient.signIn`, as the client returned
 * it.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: issue #1119. `useLogto().signIn` wraps the
 * client call in a proxy (`@logto/react` `hooks/index.js`, `proxy`) that catches
 * every error, stores it with `setError`, and RESOLVES. So on web a sign-in that
 * threw before navigating -- the OIDC config fetch failing, the sign-in session
 * write to sessionStorage throwing -- looked exactly like one that started its
 * redirect, and `onSignInSettled` could never report `rejected`. The client
 * records its own promise here and the bridge awaits it after the hook's call:
 * the proxy has already handled that rejection once, so awaiting it again
 * re-raises the error without an unhandled rejection.
 *
 * Module state rather than per-instance, because the bridge cannot reach the
 * instance `LogtoProvider` constructs. There is one web provider per app.
 */
let lastSignInAttempt: Promise<void> = Promise.resolve();

class SignInSurfacingLogtoClient extends LogtoClient {
  /**
   * The base client's OIDC config fetch, minus its memory of a failure.
   *
   * AIDEV-NOTE [MEETBALL magic-link]: @logto/client wraps `getOidcConfig` in
   * `once()`, which caches a REJECTED promise too. One failed discovery fetch
   * (a flaky connection, a blocked request) then failed every later sign-in,
   * token refresh and sign-out in the document instantly, without a request,
   * and `/auth/link`'s "Try again" could never work. This keeps a success for
   * the life of the client, as before, and forgets a failure so the next call
   * fetches again. It borrows a throwaway base client's own fetch rather than
   * reimplementing the endpoint, cache key and requester.
   */
  override readonly getOidcConfig: LogtoClient['getOidcConfig'];

  constructor(config: LogtoConfig, unstable_enableCache?: boolean) {
    super(config, unstable_enableCache);
    let pending: ReturnType<LogtoClient['getOidcConfig']> | undefined;
    this.getOidcConfig = () =>
      (pending ??= new LogtoClient(config, unstable_enableCache)
        .getOidcConfig()
        .catch((error: unknown) => {
          pending = undefined;
          throw error;
        }));
  }

  // The base class's overloads, restated so this stays a drop-in. The app only
  // ever passes the options object; the positional form is the base's own.
  override signIn(options: SignInOptions): Promise<void>;
  override signIn(
    redirectUri: SignInOptions['redirectUri'],
    interactionMode?: SignInOptions['interactionMode'],
    loginHint?: SignInOptions['loginHint'],
  ): Promise<void>;
  override signIn(...args: unknown[]): Promise<void> {
    // A pure pass-through of whatever overload the caller used, so the
    // deprecated positional one is only named here, never chosen.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const attempt = (super.signIn as (...forwarded: unknown[]) => Promise<void>).apply(this, args);
    lastSignInAttempt = attempt;
    return attempt;
  }
}

// ---------------------------------------------------------------------------
// Inner bridge: reads from @logto/react context, writes to adapter context
// ---------------------------------------------------------------------------

function WebLogtoAdapterBridge({ children }: { children: ReactNode }) {
  const logto = useLogto();

  const value = useMemo<LogtoAdapterValue>(
    () => ({
      isAuthenticated: logto.isAuthenticated,
      isLoading: logto.isLoading,
      error: logto.error,

      // Awaits the hook's call, which can never reject (see
      // `SignInSurfacingLogtoClient`), and then the attempt itself, which can.
      //
      // The attempt is read right after the call starts, not after it settles:
      // the hook's proxy calls `client.signIn` synchronously, so this is THIS
      // call's attempt, and a second sign-in started meanwhile cannot hand its
      // outcome to this one.
      signIn: async opts => {
        const hookCall = logto.signIn(opts);
        const attempt = lastSignInAttempt;
        await hookCall;
        await attempt;
      },

      // signOut already matches (postLogoutRedirectUri?: string) => Promise<void>
      signOut: async (postLogoutRedirectUri?: string) => {
        await logto.signOut(postLogoutRedirectUri);
      },

      // getAccessToken returns Optional<string> from useLogto, we need string.
      // It can return undefined when not authenticated; callers handle that via
      // checking isAuthenticated first or catching errors.
      getAccessToken: async (resource?: string, organizationId?: string) => {
        const token = await logto.getAccessToken(resource, organizationId);
        if (token === undefined) {
          throw new Error('Not authenticated');
        }
        return token;
      },

      getIdTokenClaims: async () => {
        return logto.getIdTokenClaims();
      },

      fetchUserInfo: async () => {
        const info = await logto.fetchUserInfo();
        if (info === undefined) {
          throw new Error('Not authenticated');
        }
        return info;
      },

      clearAllTokens: async () => {
        await logto.clearAllTokens();
      },
    }),
    // Only react to state changes. SDK functions (signIn, signOut, etc.) are bound to the
    // internal LogtoClient instance and don't change behavior across renders -- only their
    // wrapper reference changes because useLogto() returns a new object each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logto.isAuthenticated, logto.isLoading, logto.error],
  );

  return <LogtoAdapterContext.Provider value={value}>{children}</LogtoAdapterContext.Provider>;
}

// ---------------------------------------------------------------------------
// Public provider -- wraps LogtoProvider + bridge
// ---------------------------------------------------------------------------

export function WebLogtoAdapterProvider({ children }: { children: ReactNode }) {
  return (
    <LogtoProvider config={logtoConfig} LogtoClientClass={SignInSurfacingLogtoClient}>
      <WebLogtoAdapterBridge>{children}</WebLogtoAdapterBridge>
    </LogtoProvider>
  );
}
